import { ElementType, forwardRef, ReactElement } from "react"

import type { PolymorphicRef } from "~/shared/ui/kit/types"
import type { ButtonProps } from "~/shared/ui/kit/components/Button/ButtonProps"
import { Button } from "~/shared/ui/kit/components/Button"

import { toArray } from "~/shared/lib/utils"
import { MaterialIcon, type MaterialIconProps } from "~/shared/ui/kit/components/MaterialIcon"
import { cx } from "classix"

import cssModule from "./index.module.css"

/** Свойства `Button` + свойство `icon` для имени иконки */
export type IconButtonProps<C extends ElementType = "button"> =
	ButtonProps<C> & { icon: string, iconSize?: string | number, size?: string | number, iconProps?: Omit<MaterialIconProps, "name"> }

/**
 * IconButtonComponent
 * @template C - тип элемента, используемый в качестве кнопки
 */
export type IconButtonComponent = { displayName?: string } & (<
	C extends ElementType = "button",
>(
	props: IconButtonProps<C>,
	ref?: PolymorphicRef<C>
) => ReactElement | null)


/**
 * Кнопка `Button` с иконкой {@link MaterialIcon} внутри. Поддерживает все свойства `Button`, включая `elementType`.
 * @template C - тип элемента, используемый в качестве кнопки
 */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
export const IconButton = forwardRef(function IconButton<
	C extends ElementType = "button",
>(
	{ icon, iconSize, size = "2rem", css, style, className, iconProps = {}, ...btnProps }: IconButtonProps<C>,
	ref?: PolymorphicRef<C>
) {
	const B = Button<C>

	const s = typeof size === "number" ? size + "rem" : size

	
	return (
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-expect-error
		<B
			ref={ref}
			css={toArray(css)}
			style={{
				width: s,
				height: s,
				padding: "0",
				borderRadius: "100%",
				...style
			}}
			className={cx(cssModule.iconButton, className)}
			{...(btnProps as ButtonProps<C>)}
		>
			<MaterialIcon size={iconSize} name={icon} {...iconProps} />
		</B>
	)
}) as IconButtonComponent

IconButton.displayName = "IconButton"
